import React from 'react';
import { useIntl } from 'react-intl';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useS4aStatus } from '../hooks/useS4aStatus';
import { shortDate } from '../outreachFormat';
import { findMessages as messages } from './findMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface S4aPitchedMarkProps {
  accountId: number;
  catalogItemUuid: string;
}

/**
 * The manual mark of the Spotify editorial pitch (EMAILS.md E6): "I pitched it", and when marked
 * "Pitched on <date> · Undo". Spotify has no API, so only the artist knows that the pitch went out.
 */
const S4aPitchedMarkComponent: React.FC<S4aPitchedMarkProps> = ({ accountId, catalogItemUuid }) => {
  const intl = useIntl();
  const { status, isLoading, markPitched, unmarkPitched, isWriting, error } = useS4aStatus(accountId, catalogItemUuid);
  if (isLoading) {
    return null;
  }
  const deadline =
    status?.deadline === null || status?.deadline === undefined ? null : `${status.deadline}T00:00:00.000Z`;
  return (
    <div className="d-flex flex-column gap-1" data-testid="s4a-pitched-mark">
      {status?.pitchedAt === null || status?.pitchedAt === undefined ? (
        <>
          <div className={`${shared.row} align-items-center`}>
            <SMLButton outline omitCap leftIcon={faCheck} isLoading={isWriting} onClick={markPitched}>
              {intl.formatMessage(messages.s4aMarkPitched)}
            </SMLButton>
            {deadline !== null && (
              <span className={shared.small}>
                {intl.formatMessage(messages.s4aDeadline, {
                  date: intl.formatDate(deadline, { month: 'short', day: 'numeric', timeZone: 'UTC' }),
                })}
              </span>
            )}
          </div>
          <span className={shared.small}>{intl.formatMessage(messages.s4aMarkHint)}</span>
        </>
      ) : (
        <div className={`${shared.row} align-items-center`}>
          <span>{intl.formatMessage(messages.s4aPitchedOn, { date: shortDate(intl, status.pitchedAt) })}</span>
          <span aria-hidden>·</span>
          <SMLButton color="link" omitCap isLoading={isWriting} onClick={unmarkPitched}>
            {intl.formatMessage(messages.s4aUndo)}
          </SMLButton>
        </div>
      )}
      {error !== null && (
        <span className="text-danger">{withBrandWord(intl.formatMessage(messages.s4aMarkFailed))}</span>
      )}
    </div>
  );
};

export const S4aPitchedMark = TypedMemo(S4aPitchedMarkComponent);
